import algoliasearch from "algoliasearch/lite";
import instantsearch from "instantsearch.js";
import {
  clearRefinements,
  configure,
  searchBox,
  stats,
  infiniteHits,
  toggleRefinement,
  refinementList,
} from "instantsearch.js/es/widgets";

/* ----------------------------------
 * Helpers
 * ---------------------------------- */

// Format Unix timestamp to DD.MM.YYYY
const formatUnixToDate = (timestamp) => {
  const date = new Date(timestamp * 1000);
  return `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(2, "0")}.${date.getFullYear()}`;
};

/* ----------------------------------
 * Custom Widgets
 * ---------------------------------- */

const dateRangeWidget = (attribute) => ({
  init({ helper }) {
    const startInput = document.getElementById("start-date");
    const endInput = document.getElementById("end-date");

    const applyFilter = () => {
      const startDate = startInput.value
        ? Math.floor(new Date(startInput.value).getTime() / 1000)
        : null;
      const endDate = endInput.value
        ? Math.floor(new Date(endInput.value).getTime() / 1000) + 84600
        : null;

      helper.clearRefinements(attribute);

      if (startDate !== null)
        helper.addNumericRefinement(attribute, ">=", startDate);
      if (endDate !== null)
        helper.addNumericRefinement(attribute, "<=", endDate);

      helper.search();
    };

    startInput.addEventListener("change", applyFilter);
    endInput.addEventListener("change", applyFilter);
  },

  render({ helper }) {
    const startInput = document.getElementById("start-date");
    const endInput = document.getElementById("end-date");

    const refinements = helper
      .getRefinements(attribute)
      .filter((ref) => ref.type === "numeric");
    const gte = refinements.find((ref) => ref.operator === ">=");
    const lte = refinements.find((ref) => ref.operator === "<=");

    if (gte)
      startInput.value = new Date(gte.value * 1000).toISOString().split("T")[0];
    if (lte)
      endInput.value = new Date(lte.value * 1000).toISOString().split("T")[0];
  },

  getWidgetState(uiState, { searchParameters }) {
    const refinements = searchParameters.getNumericRefinements(attribute);
    const start = refinements[">="]?.[0];
    const end = refinements["<="]?.[0];

    if (!start && !end) return uiState;

    return {
      ...uiState,
      dateRange: { start, end },
    };
  },

  getWidgetSearchParameters(searchParameters, { uiState }) {
    const { start, end } = uiState.dateRange || {};
    let params = searchParameters.clearRefinements(attribute);

    if (start)
      params = params.addNumericRefinement(attribute, ">=", Number(start));
    if (end) params = params.addNumericRefinement(attribute, "<=", Number(end));

    return params;
  },
});

/* ----------------------------------
 * Search Initialization
 * ---------------------------------- */

export function initSearch({ appId, apiKey, indexName }) {
  const searchClient = algoliasearch(appId, apiKey);

  const search = instantsearch({
    indexName,
    searchClient,
    routing: true,
  });

  search.addWidgets([
    configure({
      filters: window.currentUserIsLoggedIn === true ? "" : "private:false",
    }),

    searchBox({
      container: "#searchbox",
      placeholder: "Search for keywords...",
      showReset: false,
      showSubmit: false,
      cssClasses: {
        form: "flex w-full",
        input: "search rounded-full",
      },
    }),

    refinementList({
      container: "#topics",
      attribute: "topics",
      templates: {
        item(item, { html }) {
          const { url, label, count, isRefined } = item;

          // Show blank label
          if (item.value === null) {
            return `<div class="no-results">No results matching current filter.</div>`;
          }

          return `
            <label class="checkbox">
              <input type="checkbox" class="peer" ${isRefined ? "checked" : ""}/>
              <span>${label} <span class="text-grey">(${count})</span></span>
            </label>
          `;
        },
      },
      transformItems(items, { results }) {
        if (items.length === 0) {
          // Return a dummy item to trigger rendering
          return [
            { label: "No results", count: 0, isRefined: false, value: null },
          ];
        }
        return items;
      },
    }),

    refinementList({
      container: "#file-kind",
      attribute: "fileKind",
      templates: {
        item(item, { html }) {
          const { url, label, count, isRefined } = item;

          // Show blank label
          if (item.value === null) {
            return `<div class="no-results">No results matching current filter.</div>`;
          }

          return `
            <label class="checkbox">
              <input type="checkbox" class="peer" ${isRefined ? "checked" : ""}/>
              <span>${label} <span class="text-grey">(${count})</span></span>
            </label>
          `;
        },
      },
      transformItems(items, { results }) {
        if (items.length === 0) {
          // Return a dummy item to trigger rendering
          return [
            { label: "No results", count: 0, isRefined: false, value: null },
          ];
        }
        return items;
      },
    }),

    infiniteHits({
      container: "#hits",
      cssClasses: {
        list: "flex flex-col gap-6",
        loadMore:
          "block cursor-pointer underline decoration-transparent hover:decoration-current transition text-center mx-auto mt-3",
        disabledLoadMore: "hidden",
      },
      templates: {
        item(hit, { html, components }) {
          const categories = (hit.categories || [])
            .map((cat) => {
              const isWhite = ["blue", "orange", "pale-light-blue"].includes(
                cat.color,
              );
              return `<span class="bg-${cat.color} ${isWhite ? "text-white" : ""} max-w-max px-2 py-1 rounded-(--m-border-radius) text-sm">${cat.title}</span>`;
            })
            .join(" ");

          const date = formatUnixToDate(hit.date);
          const summary =
            hit.summary && hit.summary.length
              ? `<p class="line-clamp-3 mb-6">${hit.summary}</p>`
              : "";
          const fileIcon = getFileIcon(hit.fileKind);

          const download =
            !hit.private || window.currentUserIsLoggedIn === true
              ? `<form method="post">
              <input type="hidden" name="action" value="madebybridge/private-files">
              <input type="hidden" name="${window.csrfParam}" value="${window.csrfToken}">
              <input type="hidden" name="path" value="${hit.fileUrl}">
              <button type="submit" class="transition-colors underline decoration-transparent hover:decoration-current text-left cursor-pointer">
                Download file (${hit.fileSize})
              </button>
            </form>`
              : `<a href="/account/login?redirect=${hit.uri}" class="transition-colors underline decoration-transparent hover:decoration-current">Please log in to access this file</a>`;

          const bookmark = ``;

          return `
            <div class="flex flex-col gap-3 p-6 bg-pale-light-blue-5 rounded-(--m-border-radius)">
              <div class="flex items-center gap-3 justify-between">
                <h3 class="text-6 font-medium"><a href="${hit.url}" class="transition-colors underline decoration-transparent hover:decoration-current">${hit.title}</a></h3>
                ${bookmark}
              </div>

              <div class="flex items-center gap-3">
                ${categories}
                <p>${date}</p>
              </div>

              ${summary}

              <div class="flex items-center gap-3">
                ${fileIcon}
                ${download}
              </div>
            </div>
          `;
        },
      },
    }),

    stats({
      container: "#stats",
    }),

    clearRefinements({
      container: "#clear-refinements",
      includedAttributes: ["query", "topics", "date", "fileKind"],
      templates: {
        resetLabel: "Clear filters",
      },
      cssClasses: {
        button:
          "cursor-pointer underline decoration-transparent hover:decoration-current transition",
      },
    }),

    dateRangeWidget("date"),
  ]);

  search.start();
}

/* ----------------------------------
 * File Type Icon Helper
 * ---------------------------------- */

function getFileIcon(kind) {
  const svgClass = "fill-orange flex-shrink-0 size-8";

  const icons = {
    pdf: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 34.875 36" class="${svgClass}"><path d="M4.5 33.75h3.375V36H4.5A4.5 4.5 0 0 1 0 31.5v-27A4.5 4.5 0 0 1 4.5 0h10.976a3.375 3.375 0 0 1 2.384.991l8.149 8.142A3.375 3.375 0 0 1 27 11.517v9.858h-2.25V13.5h-7.875a3.376 3.376 0 0 1-3.375-3.375V2.25h-9A2.248 2.248 0 0 0 2.25 4.5v27a2.248 2.248 0 0 0 2.25 2.25Zm20.215-22.5a1.073 1.073 0 0 0-.3-.52L16.27 2.58a1.106 1.106 0 0 0-.52-.3v7.84a1.128 1.128 0 0 0 1.125 1.125Zm-12.34 13.5h2.25a3.938 3.938 0 0 1 0 7.875H13.5v2.25a1.125 1.125 0 0 1-2.25 0v-9a1.128 1.128 0 0 1 1.125-1.125Zm2.25 5.625a1.688 1.688 0 0 0 0-3.375H13.5v3.375Zm6.75-5.625h2.25A3.376 3.376 0 0 1 27 28.125v4.5A3.376 3.376 0 0 1 23.625 36h-2.25a1.128 1.128 0 0 1-1.125-1.125v-9a1.128 1.128 0 0 1 1.125-1.125Zm2.25 9a1.128 1.128 0 0 0 1.125-1.125v-4.5A1.128 1.128 0 0 0 23.625 27H22.5v6.75Zm5.625-7.875a1.128 1.128 0 0 1 1.125-1.125h3.375a1.125 1.125 0 0 1 0 2.25H31.5v2.25h2.25a1.125 1.125 0 0 1 0 2.25H31.5v3.375a1.125 1.125 0 0 1-2.25 0v-9Z"/></svg>`,
    text: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 27 36" class="${svgClass}"><path d="M22.5 33.75h-18a2.248 2.248 0 0 1-2.25-2.25v-27A2.248 2.248 0 0 1 4.5 2.25h9v7.875a3.376 3.376 0 0 0 3.375 3.375h7.875v18a2.248 2.248 0 0 1-2.25 2.25Zm-5.625-22.5a1.128 1.128 0 0 1-1.125-1.125v-7.84a1.073 1.073 0 0 1 .52.3l8.15 8.145a1.105 1.105 0 0 1 .3.52ZM4.5 0A4.5 4.5 0 0 0 0 4.5v27A4.5 4.5 0 0 0 4.5 36h18a4.5 4.5 0 0 0 4.5-4.5V11.524a3.375 3.375 0 0 0-.991-2.384L17.866.991A3.375 3.375 0 0 0 15.483 0Zm2.756 16.531a1.125 1.125 0 1 0-2.145.682l3.938 12.374a1.126 1.126 0 0 0 2.159-.049L13.5 21.15l2.292 8.4a1.126 1.126 0 0 0 2.159.049l3.938-12.375a1.125 1.125 0 0 0-2.145-.682l-2.784 8.74-2.377-8.7a1.121 1.121 0 0 0-2.166 0l-2.377 8.7Z"/></svg>`,
    word: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 27 36" class="${svgClass}"><path d="M22.5 33.75h-18a2.248 2.248 0 0 1-2.25-2.25v-27A2.248 2.248 0 0 1 4.5 2.25h9v7.875a3.376 3.376 0 0 0 3.375 3.375h7.875v18a2.248 2.248 0 0 1-2.25 2.25Zm-5.625-22.5a1.128 1.128 0 0 1-1.125-1.125v-7.84a1.073 1.073 0 0 1 .52.3l8.15 8.145a1.105 1.105 0 0 1 .3.52ZM4.5 0A4.5 4.5 0 0 0 0 4.5v27A4.5 4.5 0 0 0 4.5 36h18a4.5 4.5 0 0 0 4.5-4.5V11.524a3.375 3.375 0 0 0-.991-2.384L17.866.991A3.375 3.375 0 0 0 15.483 0Zm2.756 16.531a1.125 1.125 0 1 0-2.145.682l3.938 12.374a1.126 1.126 0 0 0 2.159-.049L13.5 21.15l2.292 8.4a1.126 1.126 0 0 0 2.159.049l3.938-12.375a1.125 1.125 0 0 0-2.145-.682l-2.784 8.74-2.377-8.7a1.121 1.121 0 0 0-2.166 0l-2.377 8.7Z"/></svg>`,
    powerpoint: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 27 36" class="${svgClass}"><path d="M22.5 33.75h-18a2.248 2.248 0 0 1-2.25-2.25v-27A2.248 2.248 0 0 1 4.5 2.25h9v7.875a3.376 3.376 0 0 0 3.375 3.375h7.875v18a2.248 2.248 0 0 1-2.25 2.25Zm-5.625-22.5a1.128 1.128 0 0 1-1.125-1.125v-7.84a1.073 1.073 0 0 1 .52.3l8.15 8.145a1.105 1.105 0 0 1 .3.52ZM4.5 0A4.5 4.5 0 0 0 0 4.5v27A4.5 4.5 0 0 0 4.5 36h18a4.5 4.5 0 0 0 4.5-4.5V11.524a3.375 3.375 0 0 0-.991-2.384L17.866.991A3.375 3.375 0 0 0 15.483 0ZM9 16.875A1.128 1.128 0 0 0 7.875 18v12.375a1.125 1.125 0 0 0 2.25 0V27h3.938a5.063 5.063 0 0 0 0-10.125Zm5.063 7.875h-3.938v-5.625h3.938a2.813 2.813 0 0 1 0 5.625Z"/></svg>`,
    excel: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 27 36" class="${svgClass}"><path d="M22.5 33.75h-18a2.248 2.248 0 0 1-2.25-2.25v-27A2.248 2.248 0 0 1 4.5 2.25h9v7.875a3.376 3.376 0 0 0 3.375 3.375h7.875v18a2.248 2.248 0 0 1-2.25 2.25Zm-5.625-22.5a1.128 1.128 0 0 1-1.125-1.125v-7.84a1.073 1.073 0 0 1 .52.3l8.15 8.145a1.105 1.105 0 0 1 .3.52ZM4.5 0A4.5 4.5 0 0 0 0 4.5v27A4.5 4.5 0 0 0 4.5 36h18a4.5 4.5 0 0 0 4.5-4.5V11.524a3.375 3.375 0 0 0-.991-2.384L17.866.991A3.375 3.375 0 0 0 15.483 0Zm2.25 27h5.625v2.25H6.75Zm0-2.25V22.5h5.625v2.25Zm7.875 4.5V27h5.625v2.25Zm0-4.5V22.5h5.625v2.25Zm7.875 1.125V18a2.248 2.248 0 0 0-2.25-2.25H6.75A2.248 2.248 0 0 0 4.5 18v11.25a2.248 2.248 0 0 0 2.25 2.25h13.5a2.248 2.248 0 0 0 2.25-2.25v-3.375ZM20.25 18v2.25H6.75V18h13.5Z"/></svg>`,
    video: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40.5 27" class="${svgClass}"><path d="M4.5 2.25A2.248 2.248 0 0 0 2.25 4.5v18a2.248 2.248 0 0 0 2.25 2.25h18a2.248 2.248 0 0 0 2.25-2.25v-18a2.248 2.248 0 0 0-2.25-2.25ZM0 4.5A4.5 4.5 0 0 1 4.5 0h18A4.5 4.5 0 0 1 27 4.5v18a4.5 4.5 0 0 1-4.5 4.5h-18A4.5 4.5 0 0 1 0 22.5Zm36.52 19.92-7.27-4v-2.568l8.353 4.6a.43.43 0 0 0 .211.056.438.438 0 0 0 .436-.436V4.943a.438.438 0 0 0-.436-.436.451.451 0 0 0-.211.056L29.25 9.148V6.581l7.27-4a2.688 2.688 0 0 1 3.98 2.355v17.128a2.688 2.688 0 0 1-3.98 2.355Z"/></svg>`,
    image: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 31.5" class="${svgClass}"><path d="M4.5 2.25A2.248 2.248 0 0 0 2.25 4.5v16.411l4.76-4.761a2.818 2.818 0 0 1 3.98 0l4.76 4.76 9.26-9.26a2.818 2.818 0 0 1 3.98 0l4.76 4.76V4.5a2.248 2.248 0 0 0-2.25-2.25ZM2.25 24.089V27a2.248 2.248 0 0 0 2.25 2.25h2.911l6.75-6.75L9.4 17.74a.565.565 0 0 0-.795 0ZM27.4 13.24a.565.565 0 0 0-.795 0L10.589 29.25H31.5A2.248 2.248 0 0 0 33.75 27v-7.411ZM0 4.5A4.5 4.5 0 0 1 4.5 0h27A4.5 4.5 0 0 1 36 4.5V27a4.5 4.5 0 0 1-4.5 4.5h-27A4.5 4.5 0 0 1 0 27Zm11.25 3.375a1.125 1.125 0 1 0-.33.8 1.125 1.125 0 0 0 .33-.8Zm-4.5 0a3.375 3.375 0 1 1 .989 2.387 3.375 3.375 0 0 1-.989-2.387Z"/></svg>`,
  };

  return (
    icons[kind] ||
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 27 36" class="${svgClass}"><path d="M24.75 31.5v-18h-7.875a3.376 3.376 0 0 1-3.375-3.375V2.25h-9A2.248 2.248 0 0 0 2.25 4.5v27a2.248 2.248 0 0 0 2.25 2.25h18a2.248 2.248 0 0 0 2.25-2.25Zm-.035-20.25a1.073 1.073 0 0 0-.3-.52L16.27 2.58a1.106 1.106 0 0 0-.52-.3v7.84a1.128 1.128 0 0 0 1.125 1.125ZM0 4.5A4.5 4.5 0 0 1 4.5 0h10.976a3.375 3.375 0 0 1 2.384.991l8.149 8.142A3.375 3.375 0 0 1 27 11.517V31.5a4.5 4.5 0 0 1-4.5 4.5h-18A4.5 4.5 0 0 1 0 31.5Z"/></svg>`
  );
}
